import { useEffect, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Diagnosis } from '../../../../../packages/ui/templates/sign-off/components/diagnosis';
import { Drawer, drawerType } from '../../../../../packages/ui/organisms/drawer';
import { AddDiagnosis } from '../../video-call-screen/components/add-diagnosis';
import { Icons } from '../../../../../packages/icons';
import { colors } from '../../../../../packages/colors';
import { useDomainType } from '../../../../../hooks/useDomainType';
import { DomainTypesEnum } from '../../../../../constants/CommonConstants';
import { profileActionCreators } from '../../../../../redux/modules/profile';
import { priorityActionCreators } from '../../../../../redux/modules/priority';
import { getPriority } from '../../../../../redux/modules/priority/selectors';
import { triggerAutoPopulateCPT } from '../../../../../services/schedule/schedule.service';
import {
  getDiagnosisRecommendations,
  updateDiagnosisRecommendations,
} from '../../../../../services/report-service/reportService.service';

export const DiagnosisSection = ({ appointment, handleOpenReasoning }) => {
  const dispatch = useDispatch();

  const [openDiagnosisDrawer, setOpenDiagnosisDrawer] = useState(false);
  const [selectedDomainElement, setSelectedDomainElement] = useState(null);
  const [recommendedDiagnosis, setRecommendedDiagnosis] = useState([]);

  const {
    isLoading,
    typesPayload: { domainTypes },
    elementsPayload: { domainElements },
  } = useSelector(getPriority);

  const domainType = useMemo(() => domainTypes?.find(type => type.name === 'Diagnoses'), [domainTypes]);
  const domainTypeId = domainType?.Id;

  const [isLoadingDiagnoses, diagnoses] = useDomainType({
    type: DomainTypesEnum.DIAGNOSES,
    patientId: appointment?.participantId,
  });

  useEffect(() => {
    dispatch(priorityActionCreators.fetchPriorityDomainTypes());
  }, []);

  useEffect(() => {
    if (domainTypeId) {
      dispatch(priorityActionCreators.fetchDomainElementsByTypeId(domainTypeId));
    }
  }, [domainTypeId]);

  const handleAddClick = () => {
    setSelectedDomainElement(null);
    setOpenDiagnosisDrawer(true);
  };

  const handleUpdateClick = diagnosis => {
    const matchingDomainElement = domainElements?.find(element =>
      element?.actions?.metaData?.metaDataValues?.icd10Codes?.includes(
        diagnosis.selectedIct10code || diagnosis.icd10Code,
      ),
    );

    setSelectedDomainElement({
      ...diagnosis,
      domainElementId: matchingDomainElement?.Id || null,
      priority: {
        name: 'ACTIVE',
      },
    });
    setOpenDiagnosisDrawer(true);
  };

  const fetchDiagnosisRecommendations = async () => {
    try {
      const { data } = await getDiagnosisRecommendations({ appointmentId: appointment?.appointmentId });
      setRecommendedDiagnosis(
        data?.data?.recommendations.map(diagnosis => {
          const matchingDomainElement = domainElements?.find(element =>
            element?.actions?.metaData?.metaDataValues?.icd10Codes?.includes(diagnosis.icd10Code),
          );
          return {
            ...diagnosis,
            name: diagnosis.diagnosisName,
            selectedIct10code: diagnosis.icd10Code,
            domainElementId: matchingDomainElement?.Id || null,
            priority: {
              name: diagnosis.status,
            },
            notes: diagnosis.description,
          };
        }),
      );
    } catch (error) {
      console.error(error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: error?.data?.error || 'Could not fetch diagnosis recommendations.',
        }),
      );
    }
  };

  useEffect(() => {
    if (appointment?.appointmentId) {
      void fetchDiagnosisRecommendations();
    }
  }, [appointment?.appointmentId]);

  const onSubmit = async payload => {
    const data = {
      patientId: appointment.participantId,
      tagItems: [
        {
          assignedBy: appointment.participantName,
          domainElementId: payload.diagnosis,
          selectedIct10code: payload.icd10Code,
          metaData: {
            notes: payload.additionalNotes,
            selectedImportance: payload.status,
          },
        },
      ],
    };

    dispatch(
      profileActionCreators.createNewAssociatedTag({
        data,
        callback: async errorMsg => {
          if (!errorMsg) {
            setOpenDiagnosisDrawer(false);
            if (selectedDomainElement.recommendationId) {
              await updateDiagnosisRecommendations(
                { status: 'CONFIRMED' },
                {
                  appointmentId: appointment.appointmentId,
                  recommendationId: selectedDomainElement.recommendationId,
                },
              );
            }
            fetchDiagnosisRecommendations();
            // Check if there are no existing diagnoses
            if (diagnoses?.relatedElements?.length === 0) {
              try {
                await triggerAutoPopulateCPT(appointment.appointmentId);
              } catch (error) {
                console.error('Error while auto-associating CPT code:', error);
              }
            }
          }
        },
      }),
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap="40px">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 28,
            lineHeight: '36px',
            color: colors.neutral900,
          }}
        >
          Review Diagnoses
        </Typography>
        <span className={{ color: colors.neutral600, fontWeight: 500, fontSize: '16px', lineHeight: '24px' }}>
          A patient must have a diagnosis to bill insurance
        </span>
      </Box>
      {isLoading || isLoadingDiagnoses ? (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
        </Box>
      ) : (
        <Diagnosis
          onAddClick={handleAddClick}
          diagnosis={diagnoses}
          onUpdate={handleUpdateClick}
          recommendedDiagnosis={recommendedDiagnosis}
          handleOpenReasoning={handleOpenReasoning}
          appointmentId={appointment?.appointmentId}
          fetchDiagnosisRecommendations={fetchDiagnosisRecommendations}
        />
      )}
      <Drawer
        open={openDiagnosisDrawer}
        onClose={() => setOpenDiagnosisDrawer(false)}
        variant={drawerType.NORMAL}
      >
        <AddDiagnosis
          selectedDomainElement={selectedDomainElement}
          domainElements={domainElements}
          onSubmit={onSubmit}
          onClose={() => setOpenDiagnosisDrawer(false)}
          recommendedDiagnosis={recommendedDiagnosis}
        />
      </Drawer>
    </Box>
  );
};
